import {
  buildPrompt,
  type CardData,
  type CharacterDto,
  lintShot,
  type ProjectDto,
  type PromptContext,
  promptMode,
  type ShotData,
  type ShotDto,
  type TakeDto,
} from '@studio/shared';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  Film,
  Loader2,
  Pencil,
  RefreshCw,
  Sparkles,
  Trash2,
  Type,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ConfirmDialog, ProgressBar, SwitchRow } from '@/components/ui/controls';
import { Input, Label, Textarea } from '@/components/ui/form';
import { isActive } from '@/lib/queries';
import { cn, duration } from '@/lib/utils';
import { SectionTitle, useProjectCall } from './common';
import { ShotEditor } from './ShotEditor';

const STATUS_LABEL: Record<string, string> = {
  queued: 'Waiting',
  uploading: 'Sending to GPU',
  submitted: 'In FreeVideo queue',
  running: 'Generating',
  downloading: 'Downloading',
  succeeded: 'Done',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

function TakeStatus({ take }: { take: TakeDto }) {
  const variant =
    take.status === 'succeeded'
      ? 'success'
      : take.status === 'failed'
        ? 'destructive'
        : take.status === 'cancelled'
          ? 'secondary'
          : 'warning';
  return (
    <Badge variant={variant}>
      {isActive(take.status) && <Loader2 className="animate-spin" />}
      {STATUS_LABEL[take.status] ?? take.status}
    </Badge>
  );
}

function useElapsed(from: string | null, on: boolean) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!on) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [on]);
  return from ? now - Date.parse(from) : 0;
}

function TakeProgress({ take }: { take: TakeDto }) {
  const elapsed = useElapsed(take.startedAt, isActive(take.status));
  if (!isActive(take.status)) return null;
  return (
    <div className="space-y-1">
      <ProgressBar value={take.progress} />
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{take.waitingFor ?? take.progressNote ?? STATUS_LABEL[take.status]}</span>
        <span className="tabular">
          {Math.round(take.progress * 100)}%{take.startedAt ? ` · ${duration(elapsed)}` : ''}
        </span>
      </div>
    </div>
  );
}

/** The card's look, roughly as the video will show it. */
function CardPreview({ data, aspect }: { data: CardData; aspect: string }) {
  const bg =
    data.background === 'bamboo'
      ? 'repeating-linear-gradient(90deg,#8fb42a 0 26px,#a4c83a 26px 34px)'
      : data.background;
  return (
    <div
      className={cn(
        'grid place-items-center overflow-hidden rounded-md',
        aspect === '9:16' ? 'aspect-[9/16] w-32' : aspect === '1:1' ? 'aspect-square w-40' : 'aspect-video w-56',
      )}
      style={{ background: bg }}
    >
      <div
        className="text-center leading-none font-black"
        style={{ color: data.textColor, WebkitTextStroke: `1px ${data.outlineColor}`, fontSize: 22 }}
      >
        {data.text.split('\n').map((r, i) => (
          <div key={i} className="py-1">
            {r}
          </div>
        ))}
      </div>
    </div>
  );
}

function CardEditor({ data, onChange }: { data: CardData; onChange: (d: CardData) => void }) {
  const set = <K extends keyof CardData>(k: K, v: CardData[K]) => onChange({ ...data, [k]: v });
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Label className="mb-1 block">Words (one row per line)</Label>
        <Textarea rows={3} value={data.text} onChange={(e) => set('text', e.target.value)} className="hindi" />
      </div>
      <div>
        <Label className="mb-1 block">Seconds</Label>
        <Input
          type="number"
          min={1}
          max={10}
          step={0.5}
          value={data.seconds}
          onChange={(e) => set('seconds', Math.min(10, Math.max(1, Number(e.target.value) || 1)))}
        />
      </div>
      <div>
        <Label className="mb-1 block">Background</Label>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant={data.background === 'bamboo' ? 'default' : 'outline'}
            onClick={() => set('background', 'bamboo')}
          >
            Green stalks
          </Button>
          <input
            type="color"
            value={data.background === 'bamboo' ? '#a0c428' : data.background}
            onChange={(e) => set('background', e.target.value)}
            className="h-8 w-14 rounded-md border"
            aria-label="Background colour"
          />
        </div>
      </div>
      <label className="grid gap-1 text-sm font-medium">
        Text colour
        <input
          type="color"
          value={data.textColor}
          onChange={(e) => set('textColor', e.target.value.toUpperCase())}
          className="h-8 w-full rounded-md border"
        />
      </label>
      <label className="grid gap-1 text-sm font-medium">
        Outline colour
        <input
          type="color"
          value={data.outlineColor}
          onChange={(e) => set('outlineColor', e.target.value.toUpperCase())}
          className="h-8 w-full rounded-md border"
        />
      </label>
      <div className="sm:col-span-2">
        <SwitchRow label="Rows appear one by one" checked={data.animate} onCheckedChange={(v) => set('animate', v)} />
      </div>
    </div>
  );
}

interface Props {
  project: ProjectDto;
  shot: ShotDto;
  index: number;
  total: number;
  ctx: PromptContext;
  characters: CharacterDto[];
  expanded: boolean;
  onToggle: () => void;
}

export function ShotCard({ project, shot, index, total, ctx, characters, expanded, onToggle }: Props) {
  const call = useProjectCall(project.id);
  const [draft, setDraft] = useState(shot.data);
  const [saving, setSaving] = useState(false);
  const dirty = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectedTake, setSelectedTake] = useState<string | null>(null);
  const [rawOpen, setRawOpen] = useState(!!shot.rawPrompt);
  const [raw, setRaw] = useState(shot.rawPrompt ?? '');
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Server changes (another tab, a draft from the AI) come in when nothing is being typed.
  const serverJson = JSON.stringify(shot.data);
  useEffect(() => {
    if (!dirty.current) setDraft(shot.data);
  }, [serverJson]); // eslint-disable-line react-hooks/exhaustive-deps

  const edits = useRef(0);
  const save = async (data: ShotData | CardData) => {
    const version = edits.current;
    setSaving(true);
    await call(`/api/projects/shots/${shot.id}`, { method: 'PATCH', body: { data } });
    // Typing went on while saving: keep what is on screen (the next save sends it).
    if (edits.current === version) dirty.current = false;
    setSaving(false);
  };
  const change = (d: ShotData | CardData) => {
    setDraft(d);
    edits.current++;
    dirty.current = true;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void save(d), 700);
  };
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);

  const isShot = shot.kind === 'shot';
  const data = draft as ShotData;
  const generated = useMemo(() => (isShot ? buildPrompt(data, ctx) : ''), [isShot, data, ctx]);
  const warnings = useMemo(() => (isShot ? lintShot(data, ctx) : []), [isShot, data, ctx]);
  const mode = isShot ? promptMode(data) : null;

  const latest = shot.takes[0];
  const active = shot.takes.find((t) => isActive(t.status));
  const chosen = shot.takes.find((t) => t.id === shot.chosenTakeId);
  const shown = shot.takes.find((t) => t.id === selectedTake) ?? chosen ?? shot.takes.find((t) => t.video);
  const thumb = chosen?.thumbnail ?? chosen?.lastFrame;
  const label = isShot ? data.label : (draft as CardData).text.replace(/\n/g, ' ');

  const generate = () =>
    call(
      `/api/projects/${project.id}/generate`,
      { method: 'POST', body: { shotIds: [shot.id], missingOnly: false } },
      (r: { project: ProjectDto }) => r.project,
    );
  const remake = (newSeed: boolean) =>
    call(`/api/projects/shots/${shot.id}/remake`, { method: 'POST', body: { newSeed } });
  const move = (d: -1 | 1) => {
    const ids = project.shots.map((s) => s.id);
    const j = index + d;
    [ids[index], ids[j]] = [ids[j]!, ids[index]!];
    void call(`/api/projects/${project.id}/shots/reorder`, { method: 'POST', body: { ids } });
  };

  return (
    <Card className={cn('overflow-hidden', expanded && 'ring-2 ring-primary/30')}>
      {/* Summary row */}
      <div className="flex items-center gap-3 p-2.5">
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          aria-expanded={expanded}
        >
          {expanded ? <ChevronDown className="size-4 shrink-0" /> : <ChevronRight className="size-4 shrink-0" />}
          <div className="w-7 shrink-0 text-center text-sm font-semibold text-muted-foreground tabular">
            {index + 1}
          </div>
          <div className="grid h-16 w-10 shrink-0 place-items-center overflow-hidden rounded bg-muted">
            {isShot ? (
              thumb ? (
                <img src={thumb.url} alt="" className="size-full object-cover" />
              ) : (
                <Film className="size-4 text-muted-foreground" />
              )
            ) : (
              <Type className="size-4 text-muted-foreground" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="truncate font-medium">{label || (isShot ? 'Untitled shot' : 'Card')}</span>
              <Badge variant="outline">{isShot ? `${data.seconds} s` : `${(draft as CardData).seconds} s card`}</Badge>
              {mode === 'references' && (
                <Badge variant="secondary">
                  {data.pictures.length} picture{data.pictures.length > 1 ? 's' : ''}
                </Badge>
              )}
              {isShot && data.startFrom && <Badge variant="secondary">continues a shot</Badge>}
              {isShot && data.lines.length > 0 && (
                <Badge variant="secondary">
                  {data.lines.length} line{data.lines.length > 1 ? 's' : ''}
                </Badge>
              )}
              {warnings.length > 0 && (
                <span title={warnings.join('\n')}>
                  <AlertTriangle className="size-4 text-warning" />
                </span>
              )}
            </div>
            {isShot && data.lines[0]?.text && (
              <div className="hindi truncate text-sm text-muted-foreground">{data.lines[0].text}</div>
            )}
            {active && (
              <div className="mt-1 max-w-md">
                <TakeProgress take={active} />
              </div>
            )}
            {!active && latest?.status === 'failed' && (
              <div className="mt-0.5 truncate text-xs text-destructive">{latest.error}</div>
            )}
          </div>
        </button>
        <div className="flex shrink-0 items-center gap-1">
          {isShot && latest && <TakeStatus take={active ?? latest} />}
          {saving && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
          {isShot && !active && (
            <Button
              size="sm"
              variant={chosen ? 'outline' : 'default'}
              onClick={() => (chosen ? remake(true) : generate())}
            >
              {chosen ? <RefreshCw /> : <Sparkles />}
              <span className="hidden sm:inline">{chosen ? 'Remake' : 'Generate'}</span>
            </Button>
          )}
          {active && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => call(`/api/projects/takes/${active.id}/cancel`, { method: 'POST' })}
              title="Cancel"
            >
              <X />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            disabled={index === 0}
            onClick={() => move(-1)}
            aria-label="Move up"
          >
            <ArrowUp />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            disabled={index === total - 1}
            onClick={() => move(1)}
            aria-label="Move down"
          >
            <ArrowDown />
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="grid gap-6 border-t p-4 xl:grid-cols-[minmax(0,1fr)_380px]">
          <div className="min-w-0">
            {isShot ? (
              <ShotEditor
                data={data}
                onChange={change}
                project={project}
                shotId={shot.id}
                characters={characters}
                mode={mode!}
              />
            ) : (
              <CardEditor data={draft as CardData} onChange={change} />
            )}
            <div className="mt-6 flex flex-wrap gap-2 border-t pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => call(`/api/projects/shots/${shot.id}/duplicate`, { method: 'POST' })}
              >
                <Copy /> Duplicate
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  call(`/api/projects/${project.id}/shots`, {
                    method: 'POST',
                    body: { kind: 'shot', afterId: shot.id },
                  })
                }
              >
                + Shot after
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  call(`/api/projects/${project.id}/shots`, {
                    method: 'POST',
                    body: { kind: 'card', afterId: shot.id },
                  })
                }
              >
                + Card after
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto text-destructive"
                onClick={() => setConfirmDelete(true)}
                disabled={!!active}
              >
                <Trash2 /> Delete
              </Button>
            </div>
          </div>

          <div className="min-w-0 space-y-4">
            {!isShot && <CardPreview data={draft as CardData} aspect={project.aspect} />}
            {isShot && (
              <>
                <div>
                  <SectionTitle>Video</SectionTitle>
                  {shown?.video ? (
                    <video
                      key={shown.video.url}
                      src={shown.video.url}
                      controls
                      className={cn(
                        'w-full rounded-md bg-black',
                        project.aspect === '9:16' && 'mx-auto max-h-[520px] w-auto',
                      )}
                    />
                  ) : (
                    <div className="grid h-40 place-items-center rounded-md border border-dashed text-sm text-muted-foreground">
                      No video yet
                    </div>
                  )}
                  {active && (
                    <div className="mt-2">
                      <TakeProgress take={active} />
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {!active && !chosen && (
                      <Button size="sm" onClick={generate}>
                        <Sparkles /> Generate
                      </Button>
                    )}
                    {!active && chosen && (
                      <>
                        <Button size="sm" onClick={() => remake(true)}>
                          <RefreshCw /> Remake (new seed)
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => remake(false)}
                          title="Same seed, with the prompt as it is now"
                        >
                          Remake same seed
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                {shot.takes.length > 0 && (
                  <div>
                    <SectionTitle>Takes</SectionTitle>
                    <div className="space-y-1.5">
                      {shot.takes.map((t) => (
                        <div
                          key={t.id}
                          className={cn(
                            'flex items-center gap-2 rounded-md border p-1.5 text-sm',
                            shown?.id === t.id && 'border-primary bg-primary/5',
                          )}
                        >
                          <button
                            type="button"
                            className="flex min-w-0 flex-1 items-center gap-2 text-left"
                            onClick={() => setSelectedTake(t.id)}
                            disabled={!t.video}
                          >
                            <div className="h-10 w-7 shrink-0 overflow-hidden rounded bg-muted">
                              {t.thumbnail && <img src={t.thumbnail.url} alt="" className="size-full object-cover" />}
                            </div>
                            <span className="font-medium">#{t.number}</span>
                            <TakeStatus take={t} />
                            {t.duration && (
                              <span className="text-xs text-muted-foreground">{t.duration.toFixed(1)} s</span>
                            )}
                            {t.error && t.status === 'failed' && (
                              <span className="truncate text-xs text-destructive" title={t.error}>
                                {t.error}
                              </span>
                            )}
                          </button>
                          {t.id === shot.chosenTakeId ? (
                            <Badge>
                              <Check /> used
                            </Badge>
                          ) : t.status === 'succeeded' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7"
                              onClick={() =>
                                call(`/api/projects/shots/${shot.id}/choose`, {
                                  method: 'POST',
                                  body: { takeId: t.id },
                                })
                              }
                            >
                              Use this
                            </Button>
                          ) : isActive(t.status) ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7"
                              onClick={() => call(`/api/projects/takes/${t.id}/cancel`, { method: 'POST' })}
                            >
                              Cancel
                            </Button>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {warnings.length > 0 && (
                  <div className="space-y-1 rounded-md border border-warning/40 bg-warning/5 p-2 text-xs">
                    {warnings.map((w) => (
                      <div key={w} className="flex gap-1.5">
                        <AlertTriangle className="size-3.5 shrink-0 text-warning" /> {w}
                      </div>
                    ))}
                  </div>
                )}
                <div>
                  <div className="flex items-center justify-between">
                    <SectionTitle
                      hint={
                        mode === 'references'
                          ? 'Shot with reference pictures'
                          : data.startFrom
                            ? 'Starts from a frame'
                            : 'Text only'
                      }
                    >
                      Prompt for FreeVideo
                    </SectionTitle>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7"
                      onClick={() => {
                        if (!rawOpen) setRaw(shot.rawPrompt ?? generated);
                        setRawOpen(!rawOpen);
                      }}
                    >
                      <Pencil /> {rawOpen ? 'Close' : 'Edit raw prompt'}
                    </Button>
                  </div>
                  {shot.rawPrompt && !rawOpen && <Badge variant="warning">Hand-written prompt in use</Badge>}
                  {rawOpen ? (
                    <div className="space-y-2">
                      <Textarea
                        rows={14}
                        className="hindi text-xs leading-relaxed"
                        value={raw}
                        onChange={(e) => setRaw(e.target.value)}
                      />
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          onClick={() =>
                            call(`/api/projects/shots/${shot.id}`, { method: 'PATCH', body: { rawPrompt: raw } })
                          }
                        >
                          Use this prompt
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setRaw(generated);
                            void call(`/api/projects/shots/${shot.id}`, { method: 'PATCH', body: { rawPrompt: null } });
                            setRawOpen(false);
                          }}
                        >
                          Back to the generated prompt
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <pre className="hindi max-h-80 overflow-auto rounded-md bg-muted p-3 text-xs leading-relaxed whitespace-pre-wrap">
                      {shot.rawPrompt ?? generated}
                    </pre>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`Delete ${isShot ? 'shot' : 'card'} ${index + 1}?`}
        description={shot.takes.length ? 'Its takes (videos) are deleted too.' : undefined}
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          setConfirmDelete(false);
          void call(`/api/projects/shots/${shot.id}`, { method: 'DELETE' });
        }}
      />
    </Card>
  );
}
